import React, { useContext, useEffect, useMemo } from 'react'
import { Link, navigate } from 'gatsby'
import * as classes from './Projects.module.scss'
import cn from 'classnames'
import Container from '../../ui/Container/Container'
import SectionTitle from '../../ui/SectionTitle/SectionTitle'
import { Button } from '../../ui/Button/Button'
import { useWindowSize } from '../../../hooks/useWindowSize'
import { getPhotoSize } from '../../../utils/getPhotoSize'
import { deleteHtmlTag } from '../../../utils/deleteHtmlTag'
import { FormContext } from '../../../context'
import splitRow from '../../../utils/splitRow'
import { gsap } from 'gsap/dist/gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { startAnimationScroll } from '../../../utils/animations'
import splitTitleV2 from '../../../utils/splitTitleV2'

gsap.registerPlugin(ScrollTrigger)

function Projects({ data, lang, textLocation }) {
    const { animationStart } = useContext(FormContext)

    const windowSize = useWindowSize()
    const imageSize = getPhotoSize(windowSize.width)

    const title = useMemo(() => deleteHtmlTag(data.title.value, 'p'), [data.title.value])
    const text = useMemo(() => deleteHtmlTag(data.text.value, 'p'), [data.text.value])
    const location = useMemo(() => deleteHtmlTag(textLocation, 'p'), [textLocation])

    const getItemLink = (href) => {
        if (lang === 'hy') {
            return href
        } else {
            return `/en${href}`
        }
    }

    const photoWidth = { 360: 1000, 768: 1200, 1024: 900, 1440: 900, 1920: 900 }

    const getPhotoUrl = (url, size) => {
        if (!url) {
            return ''
        }

        return `${url}${url.includes('?') ? '&' : '?'}w=${photoWidth[size] || 900}`
    }

    const newArr = useMemo(() => {
        if (!data.items.nodes.length) {
            return []
        }

        return data.items.nodes.map((item) => {
            const url = item?.previewPictureDesktop?.url || ''

            return {
                title: item.title,
                desc: deleteHtmlTag(item.previewText, 'p'),
                urlCode: item.urlCode,
                location: item.location,
                photo: {
                    1440: getPhotoUrl(url, 1440),
                    768: getPhotoUrl(url, 768),
                    360: getPhotoUrl(url, 360)
                }
            }
        })
    }, [data.items.nodes])

    const classAnimationTitle = 'anime-title-projects-brief'
    const classAnimationText = 'anime-text-projects-brief'

    useEffect(() => {
        if (animationStart) {
            splitRow(classAnimationText)
            splitTitleV2(classAnimationTitle)
        }

        const observer = startAnimationScroll([
            { type: 'text', className: classAnimationText },
            { type: 'title', className: classAnimationTitle }
        ])

        return () => {
            if (observer) {
                observer.disconnect()
            }
        }
    }, [animationStart])

    useEffect(() => {
        const refresh = () => ScrollTrigger.refresh()

        const timer = setTimeout(() => {
            const elems = document.querySelectorAll('.projects-item-footer')

            elems.forEach((item, index) => {
                gsap.to(item, {
                    duration: 0.5,
                    opacity: 1,
                    ease: 'cubicBezier(0.25, 0.1, 0.25, 1)',
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 90%'
                    }
                })
            })

            refresh()
        }, 400)

        window.addEventListener('load', refresh)

        return () => {
            clearTimeout(timer)
            window.removeEventListener('load', refresh)
        }
    }, [])

    return (
        <section className={cn(classes.Section, { [classes.LangHY]: lang === 'hy' })}>
            <Container>
                {title || text ? (
                    <div className={classes.Header}>
                        {title ? <SectionTitle className={`${classes.Title} ${classAnimationTitle}`} title={title} lang={lang} /> : null}

                        {text ? <p className={`${classes.Desc} ${classAnimationText}`} dangerouslySetInnerHTML={{ __html: text }} /> : null}
                    </div>
                ) : null}

                {data.items.nodes.length ? (
                    <div>
                        {newArr.map((item, index) => (
                            <div className={cn(classes.Item, { [classes.LangHY]: lang === 'hy' })} key={index}>
                                <Link to={getItemLink(`/projects/${item.urlCode}/`)}>
                                    {item.photo && (item.photo[imageSize] || item.photo[1440]) ? (
                                        <div
                                            className={classes.ItemImage}
                                            style={{
                                                backgroundImage: `url(${item.photo[imageSize] ? item.photo[imageSize] : item.photo[1440]})`
                                            }}
                                        />
                                    ) : null}

                                    <div className={classes.ItemContent}>
                                        {item.title && (
                                            <h3
                                                className={`${classes.ItemTitle} ${classAnimationTitle} projects-item-title`}
                                                dangerouslySetInnerHTML={{ __html: item.title }}
                                            />
                                        )}

                                        {item.desc && (
                                            <p
                                                className={`${classes.ItemDesc} ${classAnimationText}`}
                                                dangerouslySetInnerHTML={{ __html: item.desc }}
                                            />
                                        )}

                                        <div className={`${classes.ItemFooter} projects-item-footer`}>
                                            {item.location && (
                                                <p className={classes.ItemLocation}>
                                                    {location ? location + ': ' : null}
                                                    <span dangerouslySetInnerHTML={{ __html: item.location }} />
                                                </p>
                                            )}

                                            <Button
                                                className={lang !== 'hy' ? classes.ItemDetails : classes.ItemDetailsHy}
                                                onClick={(event) => {
                                                    event.preventDefault()
                                                    event.stopPropagation()
                                                    navigate(getItemLink(`/projects/${item.urlCode}/`))
                                                }}
                                            >
                                                {lang === 'hy' ? 'Ավելին' : 'Explore More'}
                                            </Button>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                ) : null}
            </Container>
        </section>
    )
}

export default Projects
